import { type DependencyResult, getAvailableResult } from "DependencyResult";
import type { DependencyAccessValue, DependencyArgument, DependencyVisitorContext } from "DependencyVisitorContext";
import { areValuesEqual, decoratedWaitAvailable, operationTypes } from "DependencyVisitorUtils";

const equalCore: BooleanOperator = ([a, b]) => getAvailableResult(areValuesEqual(a, b));
const greaterThanCore: BooleanOperator = ([a, b]) => getAvailableResult(isGreaterThan(a, b));
const greaterThanOrEqualCore: BooleanOperator = ([a, b]) => getAvailableResult(isGreaterThanOrEqual(a, b));
const lessThanCore: BooleanOperator = ([a, b]) => getAvailableResult(isLessThan(a, b));
const lessThanOrEqualCore: BooleanOperator = ([a, b]) => getAvailableResult(isLessThanOrEqual(a, b));
const notEqualCore: BooleanOperator = ([a, b]) => getAvailableResult(!areValuesEqual(a, b));

const comparisonDecorationInfo = Object.freeze({
  operationType: operationTypes.Comparison,
  operandCount: 2,
  booleanResult: true,
});

const notEqualsDecorationInfo = Object.freeze({
  operationType: operationTypes.NotEquals,
  operandCount: 2,
  booleanResult: true,
});

function equal<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, boolean> {
  return decoratedWaitAvailable([left, right], data, context, equalCore, comparisonDecorationInfo);
}

function greaterThan<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, boolean> {
  return decoratedWaitAvailable([left, right], data, context, greaterThanCore, comparisonDecorationInfo);
}

function greaterThanOrEqual<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, boolean> {
  return decoratedWaitAvailable([left, right], data, context, greaterThanOrEqualCore, comparisonDecorationInfo);
}

function lessThan<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, boolean> {
  return decoratedWaitAvailable([left, right], data, context, lessThanCore, comparisonDecorationInfo);
}

function lessThanOrEqual<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, boolean> {
  return decoratedWaitAvailable([left, right], data, context, lessThanOrEqualCore, comparisonDecorationInfo);
}

function notEqual<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, boolean> {
  return decoratedWaitAvailable([left, right], data, context, notEqualCore, notEqualsDecorationInfo);
}

function isGreaterThan(a: unknown, b: unknown): boolean {
  return a != null && b != null && a > b;
}

function isGreaterThanOrEqual(a: unknown, b: unknown): boolean {
  return areValuesEqual(a, b) || isGreaterThan(a, b);
}

function isLessThan(a: unknown, b: unknown): boolean {
  return a != null && b != null && a < b;
}

function isLessThanOrEqual(a: unknown, b: unknown): boolean {
  return areValuesEqual(a, b) || isLessThan(a, b);
}

interface BooleanOperator {
  ([a, b]: unknown[]): DependencyResult<boolean>;
}

export default {
  equal,
  greaterThan,
  greaterThanOrEqual,
  lessThan,
  lessThanOrEqual,
  notEqual,
};
